import { useState, useRef, useEffect, useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import html2canvas from "html2canvas";
import { MessageSquarePlus, X, Camera, Loader2, CheckCircle, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

type SubmitState = "idle" | "submitting" | "analyzing" | "done";
type CapturePhase = "idle" | "selecting" | "processing";
type ScreenshotSubmitError = "retry" | "replace" | null;

/** Compress a PNG/JPEG data URL to a JPEG at reduced dimensions and quality to stay under 1MB */
function compressDataUrl(dataUrl: string, maxWidth = 1200, quality = 0.6): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** Crop a region from a full-page data URL using viewport-relative coordinates.
 * region.x/y are clientX/Y (viewport space); scrollX/Y offsets map them into document space
 * so the crop aligns correctly on scrolled pages. */
function cropDataUrlByViewport(
  dataUrl: string,
  region: { x: number; y: number; w: number; h: number; scrollX: number; scrollY: number },
  devicePixelRatio: number,
  captureScale: number
): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const effectiveScale = captureScale * devicePixelRatio;
      // Translate viewport coords to document coords by adding scroll offset
      const sx = Math.round((region.x + region.scrollX) * effectiveScale);
      const sy = Math.round((region.y + region.scrollY) * effectiveScale);
      const sw = Math.round(region.w * effectiveScale);
      const sh = Math.round(region.h * effectiveScale);
      const clampedSw = Math.min(sw, img.naturalWidth - sx);
      const clampedSh = Math.min(sh, img.naturalHeight - sy);
      if (clampedSw <= 0 || clampedSh <= 0) {
        resolve(dataUrl);
        return;
      }
      const canvas = document.createElement("canvas");
      canvas.width = clampedSw;
      canvas.height = clampedSh;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, sx, sy, clampedSw, clampedSh, 0, 0, clampedSw, clampedSh);
      resolve(canvas.toDataURL("image/jpeg", 0.7));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

interface DragRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface SelectionRect extends DragRect {
  scrollX: number;
  scrollY: number;
}

interface SnipOverlayProps {
  onCapture: (region: SelectionRect) => void;
  onCancel: () => void;
}

/** Full-screen Snipping Tool–style overlay portal */
function SnipOverlay({ onCapture, onCancel }: SnipOverlayProps) {
  const { t } = useTranslation("common");
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [rect, setRect] = useState<DragRect | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setStart({ x: e.clientX, y: e.clientY });
    setRect(null);
    setDragging(true);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging || !start) return;
    e.preventDefault();
    const x = Math.min(start.x, e.clientX);
    const y = Math.min(start.y, e.clientY);
    const w = Math.abs(e.clientX - start.x);
    const h = Math.abs(e.clientY - start.y);
    setRect({ x, y, w, h });
  }, [dragging, start]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (!dragging || !start) return;
    e.preventDefault();
    setDragging(false);
    const x = Math.min(start.x, e.clientX);
    const y = Math.min(start.y, e.clientY);
    const w = Math.abs(e.clientX - start.x);
    const h = Math.abs(e.clientY - start.y);
    if (w > 20 && h > 20) {
      onCapture({ x, y, w, h, scrollX: window.scrollX, scrollY: window.scrollY });
    } else {
      // Selection too small — exit selecting mode entirely (return to normal state)
      onCancel();
    }
  }, [dragging, start, onCapture, onCancel]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onCancel();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  const selectionStyle: React.CSSProperties | undefined = rect
    ? { left: rect.x, top: rect.y, width: rect.w, height: rect.h }
    : undefined;

  return createPortal(
    <div
      ref={overlayRef}
      data-feedback-ignore
      data-testid="snip-overlay"
      className="fixed inset-0 z-[9999] select-none"
      style={{ cursor: "crosshair", background: "rgba(0,0,0,0.35)" }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Instruction label */}
      <div
        className="absolute top-6 left-1/2 -translate-x-1/2 bg-black/80 text-white text-sm px-4 py-2 rounded-full pointer-events-none shadow-lg"
        data-testid="snip-instruction"
      >
        {t("feedback.drawBox")} &nbsp;·&nbsp;
        <kbd className="font-mono bg-white/20 px-1 rounded text-xs">Esc</kbd> {t("feedback.toCancel")}
      </div>

      {/* Cancel button */}
      <button
        data-feedback-ignore
        data-testid="button-snip-cancel"
        className="absolute top-6 right-6 bg-black/70 hover:bg-black/90 text-white rounded-full p-2 shadow-lg pointer-events-auto transition-colors"
        onClick={(e) => { e.stopPropagation(); onCancel(); }}
        aria-label={t("feedback.cancelCapture")}
      >
        <X className="w-4 h-4" />
      </button>

      {/* Selection rectangle */}
      {rect && (
        <>
          {/* Lit interior — punches through the dim */}
          <div
            className="absolute pointer-events-none"
            style={{
              ...selectionStyle,
              background: "rgba(255,255,255,0.08)",
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
            }}
            data-testid="snip-selection-interior"
          />
          {/* Blue border */}
          <div
            className="absolute pointer-events-none border-2 border-blue-400"
            style={selectionStyle}
            data-testid="snip-selection-border"
          />
        </>
      )}
    </div>,
    document.body
  );
}

export function FeedbackButton({ trigger }: { trigger?: ReactNode }) {
  const { t } = useTranslation("common");
  const [capturePhase, setCapturePhase] = useState<CapturePhase>("idle");
  const isRecaptureRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [screenshotFailed, setScreenshotFailed] = useState(false);
  const [screenshotSubmitError, setScreenshotSubmitError] = useState<ScreenshotSubmitError>(null);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const supportedTypes = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);
    if (!supportedTypes.has(file.type)) {
      toast({ title: t("feedback.selectImageError"), variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target?.result as string;
      if (!dataUrl) return;
      const compressed = await compressDataUrl(dataUrl, 1200, 0.7);
      setScreenshot(compressed);
      setScreenshotFailed(false);
      setScreenshotSubmitError(null);
    };
    reader.readAsDataURL(file);
    // Reset input so the same file can be re-selected if needed
    e.target.value = "";
  }

  /** Capture the full page and optionally crop to a viewport-relative region */
  async function captureRegion(region?: SelectionRect): Promise<string | null> {
    const captureScale = 0.6;

    /** Replace any CSS color() function values with a safe rgb() fallback.
     * html2canvas does not understand the modern color() function and throws
     * "Attempting to parse an unsupported color function 'color'" when it
     * encounters one in any stylesheet or inline style. */
    function stripUnsupportedColorFns(css: string): string {
      // Match `color(...)` with balanced parens (one level deep is enough in practice)
      return css.replace(/color\(\s*[^()]*(?:\([^()]*\)[^()]*)*\)/gi, "rgb(0, 0, 0)");
    }

    async function sanitizeClonedDocument(clonedDoc: Document, opts?: { stripInlineStyles?: boolean }) {
      try {
        const hasColorFn = (s: string) => /color\(/i.test(s);

        // Read every stylesheet already parsed and held in memory by the live
        // document. This avoids network fetches (which silently fail for
        // Vite-generated asset URLs) and guarantees the CSS is available.
        // We serialize each sheet's cssRules to text, strip color() values,
        // and inject the result as a <style> block into the clone's <head>.
        // Corresponding <link> tags in the clone are disabled so html2canvas
        // does not re-fetch and re-parse the original (still-broken) URLs.
        const liveSheets = Array.from(document.styleSheets);
        const clonedHead = clonedDoc.head ?? clonedDoc.documentElement;

        for (const sheet of liveSheets) {
          // Disable the matching <link> in the clone by href so html2canvas
          // doesn't try to parse the original stylesheet URL.
          if (sheet.href) {
            const matchingLinks = clonedDoc.querySelectorAll<HTMLLinkElement>(
              `link[rel="stylesheet"][href="${CSS.escape ? CSS.escape(sheet.href) : sheet.href}"]`
            );
            matchingLinks.forEach((l) => { try { l.disabled = true; } catch { /* ignore */ } });
          }

          let cssText: string;
          try {
            // Accessing cssRules throws SecurityError for cross-origin sheets.
            const rules = Array.from(sheet.cssRules ?? []);
            cssText = rules.map((r) => r.cssText).join("\n");
          } catch {
            // Cross-origin sheet — just disable its <link> and move on.
            continue;
          }

          if (!cssText) continue;

          if (hasColorFn(cssText)) {
            cssText = stripUnsupportedColorFns(cssText);
          }

          const styleEl = clonedDoc.createElement("style");
          styleEl.textContent = cssText;
          clonedHead.appendChild(styleEl);
        }

        // Also disable any remaining active <link rel="stylesheet"> elements in
        // the clone that weren't matched above (e.g. external CDN sheets we
        // skipped) so html2canvas never tries to fetch and parse them itself.
        clonedDoc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]').forEach((l) => {
          try { l.disabled = true; } catch { /* ignore */ }
        });

        const styleEls = clonedDoc.querySelectorAll("style");
        styleEls.forEach((styleEl) => {
          const text = styleEl.textContent;
          if (text && hasColorFn(text)) {
            styleEl.textContent = stripUnsupportedColorFns(text);
          }
        });

        const elementsWithStyle = clonedDoc.querySelectorAll<HTMLElement>("[style]");
        elementsWithStyle.forEach((el) => {
          if (opts?.stripInlineStyles) {
            el.removeAttribute("style");
            return;
          }
          const styleAttr = el.getAttribute("style");
          if (styleAttr && hasColorFn(styleAttr)) {
            el.setAttribute("style", stripUnsupportedColorFns(styleAttr));
          }
        });
      } catch (e) {
        console.warn("Failed to sanitize cloned document for screenshot:", e);
      }
    }

    const baseOptions = {
      useCORS: true,
      allowTaint: true,
      foreignObjectRendering: false,
      scale: captureScale,
      logging: false,
      ignoreElements: (el: Element) => el.hasAttribute("data-feedback-ignore"),
      onclone: async (clonedDoc: Document) => sanitizeClonedDocument(clonedDoc),
    } as const;

    function getErrorMessage(err: unknown): string {
      if (err instanceof Error) return err.message || err.name;
      if (typeof err === "string") return err;
      if (err && typeof err === "object") {
        const obj = err as { message?: unknown; name?: unknown };
        if (typeof obj.message === "string") return obj.message;
        if (typeof obj.name === "string") return obj.name;
      }
      return String(err);
    }

    function isCrossOriginImg(el: Element): boolean {
      if (el.tagName !== "IMG") return false;
      const src = (el as HTMLImageElement).currentSrc || (el as HTMLImageElement).src;
      if (!src) return false;
      if (src.startsWith("data:") || src.startsWith("blob:")) return false;
      try {
        const url = new URL(src, window.location.href);
        return url.origin !== window.location.origin;
      } catch {
        return false;
      }
    }

    async function renderCanvas(): Promise<string> {
      let canvas: HTMLCanvasElement;
      try {
        canvas = await html2canvas(document.body, baseOptions);
      } catch (renderErr: unknown) {
        const msg = getErrorMessage(renderErr);
        const isCrossOriginIssue = /tainted|cross-origin|cors/i.test(msg);
        if (isCrossOriginIssue) throw renderErr;
        console.warn("html2canvas render failed, retrying with all inline styles stripped:", msg);
        canvas = await html2canvas(document.body, {
          ...baseOptions,
          onclone: async (clonedDoc: Document) =>
            sanitizeClonedDocument(clonedDoc, { stripInlineStyles: true }),
        });
      }

      try {
        return canvas.toDataURL("image/png");
      } catch (toDataUrlErr: unknown) {
        const msg = getErrorMessage(toDataUrlErr);
        const isTaintError =
          toDataUrlErr instanceof DOMException &&
          (toDataUrlErr.name === "SecurityError" || /tainted/i.test(toDataUrlErr.message));
        if (!isTaintError) throw toDataUrlErr;
        console.warn("Canvas tainted by cross-origin content, retrying without cross-origin images:", msg);
        const cleanCanvas = await html2canvas(document.body, {
          ...baseOptions,
          ignoreElements: (el: Element) =>
            el.hasAttribute("data-feedback-ignore") || isCrossOriginImg(el),
          onclone: async (clonedDoc: Document) => sanitizeClonedDocument(clonedDoc),
        });
        return cleanCanvas.toDataURL("image/png");
      }
    }

    try {
      const rawDataUrl = await renderCanvas();

      if (!region) {
        return await compressDataUrl(rawDataUrl, 1200, 0.6);
      }

      const cropped = await cropDataUrlByViewport(rawDataUrl, region, 1, captureScale);
      return await compressDataUrl(cropped, 1200, 0.75);
    } catch (err: unknown) {
      console.warn("Screenshot capture failed:", getErrorMessage(err), err);
      return null;
    }
  }

  /** Open the dialog directly — snip mode is only entered via the in-dialog capture button */
  function handleTriggerClick() {
    setOpen(true);
  }

  /** Called when the user completes a drag-select on the overlay */
  async function handleSnipCapture(region: SelectionRect) {
    isRecaptureRef.current = false;
    setCapturePhase("processing");
    const shot = await captureRegion(region);
    setCapturePhase("idle");
    if (shot === null) {
      setScreenshotFailed(true);
    } else {
      setScreenshotFailed(false);
      setScreenshotSubmitError(null);
      setScreenshot(shot);
    }
    // Re-open dialog; description and submitState are preserved — do NOT clear them here
    setOpen(true);
  }

  /** Called when the user cancels the snip overlay (Escape, cancel button, or tiny drag) */
  function handleSnipCancel() {
    isRecaptureRef.current = false;
    setCapturePhase("idle");
    // Always return to the dialog — capture is only entered from within the dialog
    setOpen(true);
  }

  /** Enter snip mode from inside the dialog — close dialog first so it isn't in the screenshot */
  function handleCaptureScreenshot() {
    isRecaptureRef.current = true;
    setScreenshotFailed(false);
    setScreenshotSubmitError(null);
    setOpen(false);
    // Small delay so the dialog has time to close before the overlay appears
    setTimeout(() => setCapturePhase("selecting"), 250);
  }

  async function handleSubmit() {
    if (!description.trim()) {
      toast({ title: t("feedback.addDescriptionError"), variant: "destructive" });
      return;
    }
    setScreenshotSubmitError(null);
    setSubmitState("analyzing");
    try {
      const response = await apiRequest("POST", "/api/feedback", {
        description: description.trim(),
        screenshotDataUrl: screenshot,
        pageUrl: window.location.pathname,
      });
      const result = await response.json();
      if (screenshot && result?.screenshotUpload?.stored !== true) {
        setScreenshotSubmitError("retry");
        toast({
          title: t("feedback.screenshotSaveFailed"),
          description: t("feedback.screenshotSaveRetry"),
          variant: "destructive",
        });
        setSubmitState("idle");
        return;
      }
      setSubmitState("done");
    } catch (err: any) {
      const isScreenshotError =
        err?.code === "SCREENSHOT_UPLOAD_FAILED" ||
        err?.code === "SCREENSHOT_INVALID" ||
        err?.code === "SCREENSHOT_TOO_LARGE";
      if (isScreenshotError) {
        const mustReplace =
          err?.code === "SCREENSHOT_INVALID" ||
          err?.code === "SCREENSHOT_TOO_LARGE";
        setScreenshotSubmitError(mustReplace ? "replace" : "retry");
        toast({
          title: t("feedback.screenshotSaveFailed"),
          description: t(
            mustReplace
              ? "feedback.screenshotReplaceRetry"
              : "feedback.screenshotSaveRetry",
          ),
          variant: "destructive",
        });
      } else {
        toast({ title: t("feedback.submissionFailed"), description: err.message ?? t("feedback.tryAgainDesc"), variant: "destructive" });
      }
      setSubmitState("idle");
    }
  }

  function handleClose() {
    setOpen(false);
    setDescription("");
    setScreenshot(null);
    setScreenshotFailed(false);
    setScreenshotSubmitError(null);
    setSubmitState("idle");
    setCapturePhase("idle");
  }

  const isSubmitting = submitState === "submitting" || submitState === "analyzing";

  return (
    <>
      {/* Hidden file input for manual screenshot upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        className="hidden"
        aria-hidden="true"
        data-feedback-ignore
        data-testid="input-upload-screenshot"
        onChange={handleFileInputChange}
      />

      {trigger ? (
        <div onClick={handleTriggerClick} data-feedback-ignore>
          {trigger}
        </div>
      ) : (
        <button
          data-testid="button-feedback-trigger"
          data-feedback-ignore
          onClick={handleTriggerClick}
          className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium w-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          aria-label={t("feedback.submitAria")}
        >
          <MessageSquarePlus className="w-4 h-4 shrink-0" />
          <span>{t("feedback.label")}</span>
        </button>
      )}

      {/* Snip overlay — shown during "selecting" phase */}
      {capturePhase === "selecting" && (
        <SnipOverlay onCapture={handleSnipCapture} onCancel={handleSnipCancel} />
      )}

      {/* Processing indicator — briefly shown while html2canvas is running */}
      {capturePhase === "processing" && createPortal(
        <div
          data-feedback-ignore
          data-testid="snip-processing"
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
        >
          <div className="bg-background rounded-lg shadow-xl px-6 py-4 flex items-center gap-3 text-sm font-medium">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            {t("feedback.capturing")}
          </div>
        </div>,
        document.body
      )}

      <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
        <DialogContent
          data-feedback-ignore
          className="max-w-lg"
          data-testid="dialog-feedback"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-primary" />
              {t("feedback.share")}
            </DialogTitle>
            <DialogDescription>
              {t("feedback.shareDesc")}
            </DialogDescription>
          </DialogHeader>

          {submitState === "done" ? (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-500" />
              <div>
                <p className="text-lg font-semibold">{t("feedback.thankYou")}</p>
                <p className="text-sm text-muted-foreground mt-1">{t("feedback.received")}</p>
              </div>
              <Button onClick={handleClose} data-testid="button-feedback-done">{t("close")}</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Screenshot preview */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">{t("feedback.screenshot")}</Label>
                  <div className="flex items-center gap-2">
                    {screenshot && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs gap-1"
                        onClick={() => {
                          setScreenshot(null);
                          setScreenshotSubmitError(null);
                        }}
                        data-testid="button-clear-screenshot"
                      >
                        <X className="w-3 h-3" />{t("feedback.clear")}
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs gap-1"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSubmitting}
                      data-testid="button-upload-screenshot"
                    >
                      <Upload className="w-3 h-3" />
                      {t("upload")}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs gap-1"
                      onClick={handleCaptureScreenshot}
                      disabled={isSubmitting}
                      data-testid="button-capture-screenshot"
                    >
                      <Camera className="w-3 h-3" />
                      {t("feedback.capture")}
                    </Button>
                  </div>
                </div>

                <div className="relative rounded-md border overflow-hidden bg-muted/30 h-44 flex items-center justify-center">
                  {screenshot ? (
                    <img
                      src={screenshot}
                      alt={t("feedback.capturedRegion")}
                      className="w-full h-full object-contain"
                      data-testid="img-feedback-screenshot"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Camera className="w-5 h-5" />
                      <span className="text-xs">{t("feedback.noScreenshot")}</span>
                    </div>
                  )}
                  {screenshotFailed && !screenshot && (
                    <div
                      className="absolute bottom-0 inset-x-0 bg-amber-50 dark:bg-amber-950/60 border-t border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs px-3 py-1.5 flex items-center justify-between gap-2"
                      data-testid="notice-screenshot-failed"
                    >
                      <span>{t("feedback.captureFailed")}</span>
                      <button
                        type="button"
                        className="shrink-0 underline underline-offset-2 hover:text-amber-900 dark:hover:text-amber-200 font-medium transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="button-upload-screenshot-fallback"
                      >
                        {t("feedback.uploadOne")}
                      </button>
                    </div>
                  )}
                </div>
                {screenshotSubmitError && screenshot && (
                  <div
                    role="alert"
                    data-testid="feedback-screenshot-upload-error"
                    className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  >
                    <p className="font-medium">{t("feedback.screenshotSaveFailed")}</p>
                    <p className="mt-0.5 text-xs">
                      {t(
                        screenshotSubmitError === "replace"
                          ? "feedback.screenshotReplaceRetry"
                          : "feedback.screenshotSaveRetry",
                      )}
                    </p>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="feedback-description" className="text-sm font-medium">
                  {t("feedback.descriptionLabel")} <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="feedback-description"
                  placeholder={t("feedback.descriptionPlaceholder")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  disabled={isSubmitting}
                  data-testid="textarea-feedback-description"
                  className="resize-none"
                />
              </div>

              <div className="flex items-center gap-3 pt-1">
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !description.trim()}
                  className="flex-1"
                  data-testid="button-submit-feedback"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {submitState === "analyzing" ? t("feedback.aiAnalyzing") : t("feedback.submitting")}
                    </>
                  ) : (
                    t("feedback.submit")
                  )}
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  data-testid="button-cancel-feedback"
                >
                  {t("cancel")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
